import { default as Router, Route, RawLocation } from "vue-router";
import { Store } from "vuex";
import { inject, injectable } from "inversify";

import AuthStore from "./store";

/**
 * Create a guard for vue-router that checks the users roles
 *
 * To employ this solution, you have to define the `role` metadata field on
 * your routes. For example:
 *
 *     {
 *       path: '/admin',
 *       name: 'admin',
 *       component: require('@/views/AdminPage.vue'),
 *       meta: {
 *         role: 'admin'
 *       }
 *     }
 *
 * You can also specify that the user needs one of a number of roles, by using
 * the `roles` meta field:
 *
 *     meta: {
 *       roles: ['admin', 'superuser']
 *     }
 *
 * For clarity `role` and `roles` should not be combined. If they are still
 * used together, they are interpreted as if the single `role` was appended to
 * the `roles` array.
 *
 * Then the guard checks whether the user has the `admin` role before allowing
 * access to the admin page.
 *
 * Use the guard as:
 *
 *     guard = new UserRoleGuard('/');
 *     router.beforeEach(guard.before.bind(guard));
 *
 * You can additional checks, by saying
 *
 *     meta: {
 *       authorized: () => someCondition() === true
 *     }
 */
@injectable()
export class UserRoleGuard {
  /**
   * Create a guard which allows the user to only access routes he has the
   * neccesary permissions for.
   *
   * @param store Vuex store that includes the module defined in `store.js`.
   * @param redirectTarget Where to redirect the user to.
   */
  constructor(
    @inject(AuthStore) private readonly authStore: AuthStore,
    private readonly redirectTarget: RawLocation = "/"
  ) {}

  before(to: Route, from: Route, next: (target?: any) => any): void {
    if (
      !this.authStore.isLoggedIn ||
      to === this.redirectTarget ||
      to.path === this.redirectTarget
    ) {
      next();
      return;
    }

    if (this.checkAuthorization(to)) {
      next();
    } else {
      next(this.redirectTarget);
    }
  }

  /**
   * Check whether the user has all the necessary roles.
   */
  private checkAuthorization(route: Route): boolean {
    for (const record of route.matched) {
      let roles = (record.meta && record.meta.roles) || [];
      let role = record.meta && record.meta.role;
      if (role) {
        roles.push(role);
      }

      if (
        roles.length &&
        !roles.some((role: string) => this.authStore.hasRole(role))
      ) {
        return false;
      }

      if (record.meta && record.meta.authorized && !record.meta.authorized()) {
        return false;
      }
    }

    return true;
  }

  /**
   * If you do not redirect the user after the login, the vue-router
   * `beforeEach` hook is not invoked. In that case you need to add another
   * manual redirect:
   *
   *     guard.checkOnLogin(router);
   */
  checkOnLogin(router: Router): void {
    this.authStore.$watch(
      (authStore: AuthStore) => {
        return authStore.isLoggedIn;
      },
      (loggedIn: boolean) => {
        if (loggedIn) {
          this.guardLogin(router);
        }
      }
    );
  }

  private guardLogin(router: Router): void {
    if (!this.checkAuthorization(router.currentRoute)) {
      router.push(this.redirectTarget);
    }
  }
}
